import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check } from "lucide-react";
import { z } from "zod";

const emailSchema = z.string().email("Invalid email").max(255);

interface WaitlistFormProps {
  source?: string;
  className?: string;
  variant?: "default" | "hero" | "inline";
}

const WaitlistForm = ({ source = "website", className = "", variant = "default" }: WaitlistFormProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const validation = emailSchema.safeParse(email.trim());
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    setLoading(true);

    try {
      const { error: dbError } = await supabase
        .from("waitlist")
        .insert({ email: email.trim().toLowerCase(), source });

      if (dbError) {
        if (dbError.code === "23505") {
          setSuccess(true);
          toast.success("You're already on the list!");
        } else {
          throw dbError;
        }
      } else {
        setSuccess(true);
        toast.success("You've joined the waitlist!");
      }
    } catch (err) {
      console.error("Waitlist signup error:", err);
      setError("Error signing up. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`flex items-center gap-2 text-primary ${className}`}>
        <Check className="w-5 h-5" />
        <span className="font-medium">You're on the list! We'll notify you soon.</span>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-3 ${className}`}>
        <div className="flex-1">
          <Input
            type="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-full px-6"
            disabled={loading}
          />
          {error && <p className="text-destructive text-sm mt-1 ml-4">{error}</p>}
        </div>
        <Button 
          type="submit" 
          size="lg" 
          className="gap-2 text-base px-8 h-12 rounded-full"
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Join the waitlist
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </form>
    );
  }

  if (variant === "hero") {
    return (
      <form onSubmit={handleSubmit} className={`max-w-md mx-auto ${className}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-full px-6"
              disabled={loading}
            />
          </div>
          <Button 
            type="submit" 
            size="lg" 
            className="gap-2 text-base px-8 h-12 rounded-full"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Join
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
        {error && <p className="text-destructive text-sm mt-2 text-center">{error}</p>}
      </form>
    );
  }

  // Default variant
  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div>
        <Input
          type="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12"
          disabled={loading}
        />
        {error && <p className="text-destructive text-sm mt-1">{error}</p>}
      </div>
      <Button 
        type="submit" 
        className="w-full gap-2"
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            Join the waitlist
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </Button>
    </form>
  );
};

export default WaitlistForm;
